import type { ColumnDef } from "@tanstack/react-table";
import React, { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";
import styled from "styled-components/macro";
import { Auth, useAuthContext } from "../../../components/Auth";
import { ConfirmDialog } from "../../../components/ConfirmDialog/ConfirmDialog";
import { IDMobile } from "../../../components/IDs/IDs";
import { Modal } from "../../../components/Modal/Modal";
import { StatusRight } from "../../../components/Status/Status";
import type { StatusColor } from "../../../components/Status/Status";
import { endpoints } from "../../../endpoints";
import { PageWrapper } from "../../../layout/portalPageLayout";
import type {
  SampleRequest,
  SampleRequestDetail,
  SampleRequestPaginatedOutput,
  SampleRequestStatus,
  StatusAndId,
  User,
} from "../../../types/types";
import { useRoutePath } from "../../../util/Routing";
import {
  formatDateTime,
  useInAppNotifications,
  useStoreState,
  rowHover,
  DEFAULT_PER_PAGE,
} from "../../../util/util";
import { TrimmedName } from "../../admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/AssetsUtil";
import {
  TooltipCell,
  update_status_string_list,
} from "../../Buyer/BuyerOrders/BuyerOrders.util";
import { SellerCreateSampleRequest } from "../../Seller/SellerCreateSampleRequest/SellerCreateSampleRequest";
import {
  getAccountManagerCSM,
  useAccountManagersCSMFilters,
} from "../SellerOrdersListPage/util";
import { useListPage } from "../shared";
import { TablePageLayout } from "../../../components/TablePage/TablePageLayout";
import { FiltersDropDown } from "../../../components/FiltersDropDown/FiltersDropDown";
import {
  PrimaryButtonWithPlusIcon,
  SecondaryButtonLargeV2,
} from "../../../components/Buttons/Buttons";
import { SlideOut } from "../../../components/SlideOut/SlideOut";
import { DownloadReport } from "../SellerOrdersListPage/DownloadReport/DownloadReport";

const TableWrapper = styled.div<{ is_seller: boolean }>`
  table {
    td:nth-child(1),
    td:nth-child(2),
    td:nth-child(3),
    td:nth-child(5) {
      max-width: ${({ is_seller }) => (is_seller ? "140px" : "unset")};
    }
  }
`;

type SampleRequestRow = {
  // The `id` is not shown, but is needed to handle clicks on the row.
  id: string;
  number: string;
  from: string;
  products: { items: SampleRequestDetail[]; id: string };
  requested_date: string;
  last_activity: string;
  email: string;
  company_name: string;
  phone_number: string;
  destination: string;
  status: StatusAndId<SampleRequestStatus>;
  unread: boolean;
  unread_message_count: number;
  account_manager_csm: string;
};

type StatusItem = {
  color: StatusColor;
  text: string;
  value: SampleRequestStatus;
};

// TODO can this be combined with a different status function?
export const SampleRequestStatusLabel = ({
  status,
  id,
  t,
  roleIsSeller,
}: {
  status: SampleRequestStatus;
  id?: string;
  t: (s: string) => string;
  roleIsSeller?: boolean;
}) => {
  switch (status) {
    case "requested":
    case "new":
      return <StatusRight color="blue" text={t("New")} id={id} />;
    case "responded":
      return <StatusRight color="fullBlue" text={t("Responded")} id={id} />;
    case "price_received":
      return (
        <StatusRight color="fullBlue" text={t("Price Received")} id={id} />
      );
    case "accepted":
      return <StatusRight color="green" text={t("Accepted")} id={id} />;
    case "rejected":
      return <StatusRight color="red" text={t("Declined")} id={id} />;
    case "cancelled":
      return <StatusRight color="red" text={t("Cancelled")} id={id} />;
    case "in_progress":
      return <StatusRight color="fullBlue" text={t("In Progress")} id={id} />;
    case "payment_received":
      return (
        <StatusRight color="fullBlue" text={t("Payment Received")} id={id} />
      );
    case "invoiced":
      return <StatusRight color="red" text={t("Invoiced")} id={id} />;
    case "pending":
    case "pending_activation":
      return (
        <StatusRight color="gray" text={t("Pending Activation")} id={id} />
      );
    case "pending_cancellation":
      return (
        <StatusRight color="gray" text={t("Pending Cancellation")} id={id} />
      );
    case "completed":
      return <StatusRight color="green" text={t("Completed")} id={id} />;
    case "shipped":
      return <StatusRight color="blue" text={t("Shipped")} id={id} />;
    default:
      return <StatusRight color="gray" text={status} id={id} />;
  }
};

const getProductsAsText = (
  items: SampleRequestDetail[],
  t: (s: string) => string
) => {
  if (items.length === 1) {
    return `${items[0].product.name}`;
  }
  if (items.length > 1) {
    return `${items[0].product.name} + ${items.length - 1} ${t("more")}`;
  }
  return `--`;
};

const statusList = (
  t: (val: string) => string,
  roleIsSomeKindOfSeller: boolean
): StatusItem[] => [
  { color: "blue" as StatusColor, text: t("New"), value: "requested" },
  {
    color: "fullBlue" as StatusColor,
    text: roleIsSomeKindOfSeller ? t("Responded") : t("Price Received"),
    value: "responded",
  },
  { color: "green" as StatusColor, text: t("Accepted"), value: "accepted" },
  {
    color: "fullBlue" as StatusColor,
    text: t("In Progress"),
    value: "in_progress",
  },
  {
    color: "fullBlue" as StatusColor,
    text: t("Invoiced/Payment Received"),
    value: "invoiced",
  },
  { color: "fullBlue" as StatusColor, text: t("Shipped"), value: "shipped" },
  { color: "green" as StatusColor, text: t("Completed"), value: "completed" },
  ...(roleIsSomeKindOfSeller
    ? [
        {
          color: "gray" as StatusColor,
          text: t("Pending Activation"),
          value: "pending_activation" as SampleRequestStatus,
        },
      ]
    : []),
  {
    color: "gray" as StatusColor,
    text: t("Pending Cancellation"),
    value: "pending_cancellation",
  },
  { color: "red" as StatusColor, text: t("Declined"), value: "rejected" },
  { color: "red" as StatusColor, text: t("Cancelled"), value: "cancelled" },
];
const getSampleRequestItemsList = (items: SampleRequestDetail[]) => {
  if (items && items.length > 1) {
    return (
      <ul>
        {items.map((item, index) => (
          <li key={index}>{item.product.name}</li>
        ))}
      </ul>
    );
  }
  return null;
};

/**
 * Shows a list of sample requests. Used for producer sellers and seller admins and buyers.
 */
export const SampleRequestListPage = () => {
  const { t } = useTranslation();
  const { storefront_id } = useStoreState();
  const { user } = useContext(Auth);
  const { samplesNotifications } = useInAppNotifications(
    storefront_id,
    user as User
  );
  const { accountPath } = useRoutePath();
  const { roleIsSomeKindOfSeller, roleIsSomeKindOfBuyer } = useAuthContext();

  const { accountManagers, customerServiceReps } =
    useAccountManagersCSMFilters("samples");

  // Helper functions
  const columns: ColumnDef<SampleRequestRow>[] = [
    {
      header: t("Sample ID"),
      accessorKey: "number",
      cell: (cell) => <IDMobile>{`${cell.getValue()}`}</IDMobile>,
    },
    {
      header: t("Products"),
      accessorKey: "products",
      cell: (cell) => {
        const cellValue = cell.getValue() as {
          items: SampleRequestDetail[];
          id: string;
        };
        return (
          <TooltipCell data-for={`${cellValue.id}-tooltip`} data-tip={""}>
            <TrimmedName text={getProductsAsText(cellValue.items, t)} />
            <ReactTooltip
              id={`${cellValue.id}-tooltip`}
              place="top"
              data-html={true}
              effect="solid"
              backgroundColor="#60676F"
              multiline={true}
            >
              {getSampleRequestItemsList(cellValue.items)}
            </ReactTooltip>
          </TooltipCell>
        );
      },
    },
    ...(roleIsSomeKindOfBuyer
      ? [
          {
            header: t("Sample Requested"),
            accessorKey: "requested_date",
          },
          {
            header: t("Last Activity"),
            accessorKey: "last_activity",
          },
        ]
      : roleIsSomeKindOfSeller
      ? [
          {
            header: t("From"),
            accessorKey: "from",
          },
          {
            header: t("Company Name"),
            accessorKey: "company_name",
          },
          {
            header: t("Last Activity"),
            accessorKey: "last_activity",
          },
        ]
      : []),
    {
      header: t("Destination"),
      accessorKey: "destination",
    },
    {
      header: t("Status"),
      accessorKey: "status",
      size: 50,
      minSize: 50,
      cell: (cell) => {
        return (
          <SampleRequestStatusLabel
            status={
              (cell.getValue() as StatusAndId<SampleRequestStatus>).status
            }
            id={(cell.getValue() as StatusAndId<string>).id}
            t={t}
            roleIsSeller={roleIsSomeKindOfSeller}
          />
        );
      },
    },
  ];

  const custom_columns: { [accessorKey: string]: number } =
    roleIsSomeKindOfBuyer
      ? { unread_message_count: 4 }
      : { account_manager_csm: 4, unread_message_count: 5 };

  // Data transformation functions
  const dataTransformer = useCallback(
    (response: SampleRequestPaginatedOutput): SampleRequestRow[] => {
      const getUnread = (sampleRequest: SampleRequest): boolean => {
        return samplesNotifications?.ids
          ? samplesNotifications?.ids.includes(sampleRequest?.id)
          : false;
      };

      return response.data.map(
        (sampleRequest): SampleRequestRow => ({
          id: sampleRequest.id || "--",
          number: sampleRequest.number || "--",
          from: sampleRequest.buyer_company_name || "--",
          products: { items: sampleRequest.items, id: sampleRequest.id },
          requested_date: formatDateTime(sampleRequest.created_at) || "--",
          last_activity: formatDateTime(sampleRequest.modified_at) || "--",
          email:
            sampleRequest.shipping_address?.points_of_contact?.[0]
              ?.email_address || "--",
          company_name: sampleRequest.shipping_address?.company_name || "--",
          phone_number:
            sampleRequest.shipping_address?.points_of_contact?.[0]
              ?.phone_number || "--",
          destination: `${sampleRequest.shipping_address?.city || "--"}, ${
            sampleRequest.shipping_address?.state || "--"
          }`,
          status: {
            status: sampleRequest.status || "--",
            id: sampleRequest.id,
          },
          unread: getUnread(sampleRequest),
          unread_message_count: sampleRequest.unread_message_count,
          account_manager_csm: getAccountManagerCSM(sampleRequest),
        })
      );
    },
    [samplesNotifications?.ids]
  );

  const paginationTransformer = useCallback(
    (response: SampleRequestPaginatedOutput) => ({
      total: response.pagination.total,
      offset: response.pagination.offset,
    }),
    []
  );

  const getUnreadIds = useCallback(
    (sampleRequest: SampleRequestRow) => sampleRequest.unread,
    []
  );

  const requestParams = useMemo(
    () => ({ source: "logged_in", order_by: "desc" }),
    []
  );

  // Use the shared list page hook
  const {
    state,
    data: tableData,
    isLoading,
    error,
    mutate,
    handlers,
    tableColumns,
  } = useListPage({
    defaultPerPage: DEFAULT_PER_PAGE,
    perPageOptions: [10, 20, 50],
    endpoint: endpoints.v1_storefronts_id_sampleRequests(storefront_id),
    requestParams,
    statusTransformer: update_status_string_list,
    dataTransformer,
    paginationTransformer,
    getUnreadIds,
    notifications: samplesNotifications,
    statusFilters: statusList(t, roleIsSomeKindOfSeller),
    accountManagerFilters: accountManagers,
    customerServiceRepFilters: customerServiceReps,
    columns,
    custom_columns,
    basePath: `${accountPath}/samples`,
    searchPlaceholder: t("Search by Product, Customer, or ID"),
  });

  // Row click handler
  const handleRowClick = (
    e: React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => {
    handlers.handleRowClick(e, e.currentTarget.id, `${accountPath}/samples`);
  };

  return (
    <PageWrapper>
      <TableWrapper is_seller={roleIsSomeKindOfSeller}>
        <TablePageLayout
          showTitle
          title={t("Samples")}
          searchQuery={state.searchQuery}
          searchPlaceholder={t("Search by Product, Customer, or ID")}
          onSearchChange={handlers.handleSearch}
          onSearchClear={handlers.handleClearSearch}
          toolbarActions={
            roleIsSomeKindOfSeller ? (
              <>
                <SecondaryButtonLargeV2
                  onClick={() => state.setDownloadReportSlideOut(true)}
                >
                  {t("Download Report")}
                </SecondaryButtonLargeV2>
                <PrimaryButtonWithPlusIcon
                  onClick={() => state.setShowCreateModal(true)}
                  useV2
                >
                  {t("Create New")}
                </PrimaryButtonWithPlusIcon>
              </>
            ) : undefined
          }
          filtersSlot={
            <>
              <FiltersDropDown
                activeItems={state.selectedStatus}
                applyStatus={handlers.applyStatus}
                list={statusList(t, roleIsSomeKindOfSeller)}
              />
              {roleIsSomeKindOfSeller && accountManagers.length > 0 && (
                <FiltersDropDown
                  activeItems={state.selectedAccountManagers}
                  applyStatus={handlers.applyAccountManager}
                  list={accountManagers}
                  is_status={false}
                  header={t("Account Manager")}
                />
              )}
              {roleIsSomeKindOfSeller && customerServiceReps.length > 0 && (
                <FiltersDropDown
                  activeItems={state.selectedCustomerServiceReps}
                  applyStatus={handlers.applyCustomerServiceRep}
                  list={customerServiceReps}
                  is_status={false}
                  header={t("Customer Success Manager")}
                />
              )}
            </>
          }
          perPage={state.perPage}
          perPageItems={state.perPageItems}
          onPerPageChange={handlers.changePerPage}
          columns={tableColumns}
          data={tableData}
          isLoading={isLoading}
          error={error}
          rowClick={handleRowClick}
          rowHover={rowHover}
          pagination={state.tablePagination}
          totalCount={state.tablePagination.totalCount}
          offset={state.offset}
          onPageChange={handlers.changePage}
        >
          <Modal
            show={state.showCreateModal}
            closeModal={() => state.setShowDialogBox(true)}
            modalWidth={"95%"}
          >
            <SellerCreateSampleRequest
              onSuccess={(sampleID) => {
                state.setShowCreateModal(false);
                mutate();
                // Navigate to the created sample request
                window.location.href = `${accountPath}/samples/${sampleID}`;
              }}
            />
          </Modal>

          <ConfirmDialog
            show={state.showDialogBox}
            confirmMessage={t(
              "Are you sure you want to cancel this sample request?"
            )}
            closeDialog={() => state.setShowDialogBox(false)}
            handleConfirm={() => {
              state.setShowCreateModal(false);
              state.setShowDialogBox(false);
            }}
          />

          {roleIsSomeKindOfSeller && (
            <SlideOut
              closeFlyout={handlers.closeDownloadReportSlideout}
              show={state.downloadReportSlideOut}
              title={t("Download Sample Report")}
            >
              <DownloadReport
                closeSlideout={handlers.closeDownloadReportSlideout}
                transactionType="sample"
              />
            </SlideOut>
          )}
        </TablePageLayout>
      </TableWrapper>
    </PageWrapper>
  );
};
